import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/_atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { IconInvisible } from "@/components/_icons/IconInvisible";
import { MenuDotsButton } from "@/components/_molecules/menu-dots-button";
import { Link } from "@/components/_molecules/navigation-controls/link";
import { SidebarNavElement } from "@/components/_templates/sidebar/sidebar-nav-element";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { TeamIcon } from "@/domain/team/components/team-icon";
import { makeSharedTeamUrl, makeTeamSettingsUrl, makeTeamUrl } from "@/domain/team/lib/make-team-url";
import { TeamWithoutMembershipList } from "@/domain/team/models/team-model";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { cn } from "@/lib/cn";

import { useTeamNavActiveState } from "./use-team-nav-active-state";

interface SidebarAppTeamNavElementProps {
  workspace: Workspace;
  team: TeamWithoutMembershipList;
  expanded?: boolean;
  isShared?: boolean;
  sharedEntityTypeSet?: Set<string>;
}

export const SidebarAppTeamNavElement = ({
  workspace,
  team,
  isShared,
  sharedEntityTypeSet,
  expanded: initialExpanded = false,
}: SidebarAppTeamNavElementProps) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [showDropdown, setShowDropdown] = useState(false);
  const { t } = useDomainTranslation("common");

  const { isClimateActionTeamContext, isProjectTeamContext, teamId } = useTeamNavActiveState(team);

  const { hasPermission: hasPermissionToManageTeam } = useCheckPermission({
    resource: "team",
    resourceId: team.id,
    action: "manage",
    enabled: !isShared,
  });

  useEffect(() => {
    if (teamId === team.id) {
      setExpanded(true);
    }
  }, [team, teamId]);

  return (
    <div className="group ">
      <div className="relative cursor-pointer rounded-md">
        <div
          className={cn([
            "flex w-full select-none flex-row items-center gap-1 overflow-hidden rounded-md pr-1 text-sm hover:bg-foreground/3 hover:text-accent-foreground",
            showDropdown ? "bg-foreground/3  text-accent-foreground" : "",
          ])}
        >
          <Button
            variant="ghost"
            aria-expanded={expanded}
            className="flex h-[30px] min-w-0 shrink select-none flex-row items-center justify-start overflow-hidden rounded-md p-0 pl-1.5 text-sm font-normal hover:bg-transparent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
            onClick={() => {
              setExpanded(!expanded);
            }}
          >
            <TeamIcon team={team} className="mr-2" />
            <div className="pointer-events-none min-w-0 truncate text-left text-sm">{team.name}</div>
          </Button>
          {!isShared && (
            <DropdownMenu open={showDropdown} onOpenChange={setShowDropdown}>
              <DropdownMenuTrigger asChild>
                <MenuDotsButton
                  variant="ghost"
                  className="h-6 w-6 shrink-0 hover:bg-foreground/5 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
                  aria-label={t("sidebar.teamActionsOpen", { teamName: team.name })}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <Link to={makeTeamSettingsUrl({ workspace, team, path: "general" })}>
                  <DropdownMenuItem className="cursor-pointer">{t("sidebar.teamSettings")}</DropdownMenuItem>
                </Link>
                {!hasPermissionToManageTeam && (
                  <Link to={makeTeamSettingsUrl({ workspace, team, path: "members" })}>
                    <DropdownMenuItem className="cursor-pointer">{t("sidebar.viewMembers")}</DropdownMenuItem>
                  </Link>
                )}

                {hasPermissionToManageTeam && (
                  <>
                    <DropdownMenuSeparator />
                    <Link to={makeTeamSettingsUrl({ workspace, team, path: "members" })}>
                      <DropdownMenuItem className="cursor-pointer">{t("sidebar.manageMembers")}</DropdownMenuItem>
                    </Link>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <div className="grow"></div>
          <Button
            variant="ghost"
            size="icon"
            tabIndex={-1}
            aria-hidden="true"
            className="h-6 w-6 shrink-0 hover:bg-transparent"
            onClick={() => {
              setExpanded(!expanded);
            }}
          >
            <ChevronDown className={`h-4 w-4 text-muted-foreground ${expanded ? "" : "-rotate-90"}`} />
          </Button>
        </div>
      </div>
      {expanded && (
        <>
          {(!sharedEntityTypeSet || sharedEntityTypeSet.has("CLIMATE_ACTION")) && (
            <SidebarNavElement
              to={(isShared ? makeSharedTeamUrl : makeTeamUrl)({ workspace, team, path: "climate-actions" })}
              forceActive={isClimateActionTeamContext}
              forceInactive={isProjectTeamContext}
            >
              <IconInvisible className="ml-4 mr-2 h-2.5 w-2.5" />
              {t("sidebar.climateActions")}
            </SidebarNavElement>
          )}
          {(!sharedEntityTypeSet || sharedEntityTypeSet.has("PROJECT")) && (
            <SidebarNavElement
              to={(isShared ? makeSharedTeamUrl : makeTeamUrl)({ workspace, team, path: "projects" })}
              forceActive={isProjectTeamContext}
            >
              <IconInvisible className="ml-4 mr-2 h-2.5 w-2.5" />
              {t("sidebar.projects")}
            </SidebarNavElement>
          )}
          {(!sharedEntityTypeSet || sharedEntityTypeSet.has("TASK")) && (
            <SidebarNavElement to={(isShared ? makeSharedTeamUrl : makeTeamUrl)({ workspace, team, path: "tasks" })}>
              <IconInvisible className="ml-4 mr-2 h-2.5 w-2.5" />
              {t("sidebar.tasks")}
            </SidebarNavElement>
          )}
        </>
      )}
    </div>
  );
};
